import React, { FocusEvent, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useFocus, useFocusVisible, useId } from 'react-aria';
import { genericMemo } from '@direct-frontend/stdlib';
import { useLogger } from '@direct-frontend/modules-logger';
import { isDesktop, isMobile } from '@direct-frontend/platform-code';

import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { cn } from '../../lib/css/className/className';
import { TagProps } from '../Tag';
import { TextGeneric } from '../TextGeneric/TextGeneric';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { useHStack } from '../../hooks/useStack';

import { TagTextInputEditor, type TagEditorProps } from './TagEditor';
import { TagElement } from './TagElement';
import type { TagGroupProps } from './types';
import { TagGroupContext } from './utils';

import './TagGroup.css';

const cls = cn('TagGroup');
const scrollClass = getScrollbarClassName();

/**
 * Компонент списка тегов с возможностью добавления новых
 */
export const TagGroup = genericMemo(function TagGroup<TagSourceT, DataT = unknown>({
    id,
    tags = [],
    placeholder,
    className,
    onAdd,
    onEdit,
    onDelete,
    onTagSelect,
    tagEditorFactory,
    validationState,
    isDisabled,
    isEditable,
    'data-testid': dataTestId = 'TagGroup',
    isAddDisabled,
    onFocus,
    onBlur,
    onClick,
    counterValue,
    size = 'm',
    logNode,
    direction = 'horizontal',
    maxInputHeight,
    addonRight,
    ...props
}: TagGroupProps<TagSourceT, DataT>) {
    const { LogProvider, logClick } = useLogger('TagGroup', logNode);
    // флаг видимости редактора тегов
    const [isTagEditorVisible, setTagEditorVisible] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isFocused, setFocused] = useState(false);
    const [selectedTagIndex, setSelectedTagIndex] = useState(-1);
    const { isFocusVisible } = useFocusVisible({ isTextInput: true });
    const ref = useRef<HTMLDivElement>(null);
    const tagsWrapperRef = useRef<HTMLDivElement>(null);
    const tagLauncherId = useId(id);
    const initialTagsRef = useRef(tags);

    const scrollToBottom = useCallback(() => {
        if (!maxInputHeight) {
            return;
        }

        if (tagsWrapperRef.current) {
            requestAnimationFrame(() => {
                const wrapper = tagsWrapperRef.current;
                if (wrapper) {
                    wrapper.scrollTop = wrapper.scrollHeight;
                }
            });
        }
    }, [maxInputHeight]);

    const setTagEditorVisibleHandler = useCallback(
        (isVisible: boolean) => {
            if (!isDisabled) {
                setTagEditorVisible(isVisible);
                // Делаем слепок выбранных тэгов до открытия мобильного drawer
                // для потенциальной отмены изменений
                initialTagsRef.current = tags;
                if (isVisible) {
                    // Скроллим к концу при появлении редактора
                    scrollToBottom();
                }
            }
        },
        [setTagEditorVisible, isDisabled, scrollToBottom, tags],
    );

    // Скроллим к инпуту при изменении inputValue
    useEffect(() => {
        if (isTagEditorVisible && inputValue) {
            scrollToBottom();
        }
    }, [inputValue, isTagEditorVisible, scrollToBottom]);

    const tagSelectHandler = useCallback(() => {
        const tagText = tags[selectedTagIndex]?.text ?? '';

        setInputValue(tagText);
        onTagSelect?.(tagText);
    }, [onTagSelect, selectedTagIndex, tags]);

    const focusHandler = useCallback(
        (event: FocusEvent) => {
            setFocused(true);
            tagSelectHandler();
            onFocus?.(event);
        },
        [onFocus, tagSelectHandler],
    );

    const blurHandler = useCallback(
        (event?: FocusEvent) => {
            setSelectedTagIndex(-1);
            setInputValue('');
            setTagEditorVisibleHandler(false);
            setFocused(false);
            onBlur?.(event);
        },
        [onBlur, setTagEditorVisibleHandler],
    );

    const addHandler = useCallback(
        (value: TagSourceT) => {
            if (selectedTagIndex === -1) {
                onAdd?.(value);
            } else {
                onEdit?.(value, selectedTagIndex);
                setSelectedTagIndex(-1);
            }
        },
        [onAdd, onEdit, selectedTagIndex],
    );

    const onTagClose = useCallback(
        (tagProps: TagProps, index: number) => {
            const value = typeof tagProps.tag === 'string' ? tagProps.tag : index;
            logClick({ type: 'onDelete', value: value });
            onDelete?.(index);
            tagProps.onClose?.();
        },
        [onDelete, logClick],
    );

    const tagEditor = useMemo(() => {
        const editorProps: TagEditorProps<TagSourceT, DataT> = {
            isDisabled,
            onAdd: addHandler,
            onFocus: focusHandler,
            onBlur: blurHandler,
            onChange: setInputValue,
            onRemove: onTagClose,
            clearTagIndex: () => setSelectedTagIndex(-1),
            selectedTagIndex,
            setSelectedTagIndex,
            inputValue,
            'data-testid': `${dataTestId}.editor`,
            containerRef: ref,
            className: selectedTagIndex === -1 ? cls('editor') : undefined,
            tagGroupSize: size,
            tags,
            initialTags: initialTagsRef.current,
            maxInputHeight,
        };

        return tagEditorFactory?.(editorProps);
    }, [
        isDisabled,
        addHandler,
        focusHandler,
        blurHandler,
        onTagClose,
        selectedTagIndex,
        inputValue,
        dataTestId,
        size,
        tags,
        tagEditorFactory,
        maxInputHeight,
    ]);

    const tagEditorIsExists = !isAddDisabled && Boolean(tagEditor);

    const { focusProps } = useFocus({
        isDisabled,
        onFocus: () => setTagEditorVisibleHandler(tagEditorIsExists),
    });

    const onClickHandler = useCallback(() => {
        setTagEditorVisibleHandler(tagEditorIsExists);
        onClick?.();
    }, [onClick, tagEditorIsExists, setTagEditorVisibleHandler]);

    // добавление aria-свойств, полученных из FormField
    const ariaProps = {
        'aria-label': props['aria-label'],
        'aria-required': props['aria-required'],
        'aria-invalid': props['aria-invalid'] || validationState === 'invalid',
        'aria-labelledby': props['aria-labelledby'],
        'aria-errormessage': props['aria-errormessage'],
        'aria-describedby': props['aria-describedby'],
    };

    const hasCounter = counterValue !== undefined;
    const limitExceeded = Boolean(counterValue && counterValue < 0);

    const counter = hasCounter && (
        <TextGeneric
            textSize={'16-24'}
            color={limitExceeded && !isDisabled ? 'red' : 'gray-disabled'}
            className={cls('counter')}
        >
            {counterValue}
        </TextGeneric>
    );

    const addonsStackClasses = useHStack({
        gap: 'current-geometry',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    });
    const addonStackClasses = useHStack({ alignItems: 'center' });

    const sizeSkinClass = getSizeSkin(size);
    const colorSkinClass = getColorSkin('gray-transparent-contour', {
        isDisabled,
    });
    const contextValue = useMemo(
        () => ({ isEditable, isDisabled, size, 'data-testid': dataTestId }),
        [dataTestId, isDisabled, isEditable, size],
    );
    const onTagClick = useCallback(
        (index: number) => {
            setTagEditorVisibleHandler(tagEditorIsExists);
            setSelectedTagIndex(index);
        },
        [setTagEditorVisibleHandler, setSelectedTagIndex, tagEditorIsExists],
    );

    /* Клик по аддону не должен открывать редактор тегов */
    const onAddonClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
    }, []);

    const containerStyle = useMemo((): (React.CSSProperties & Record<string, string>) | undefined => {
        if (!maxInputHeight) {
            return undefined;
        }

        return {
            '--max-input-height': maxInputHeight,
        };
    }, [maxInputHeight]);

    return (
        <LogProvider>
            <TagGroupContext.Provider value={contextValue}>
                <div
                    onClick={onClickHandler}
                    className={cls(
                        {
                            focused: isFocused && !isAddDisabled,
                            keyboardFocused: isFocusVisible && isFocused && !isAddDisabled,
                            hasError: validationState === 'invalid' || limitExceeded,
                            disabled: isDisabled,
                            size,
                            vertical: direction === 'vertical',
                        },
                        [sizeSkinClass, colorSkinClass, className],
                    )}
                    data-testid={dataTestId}
                    ref={ref}
                    style={containerStyle}
                    {...ariaProps}
                >
                    {!isTagEditorVisible && !tags.length ? (
                        <TextGenericWithSkin
                            data-testid={`${dataTestId}.placeholder`}
                            color={'gray-disabled'}
                            className={cls('placeholder')}
                            skinSize={size}
                            textSize="current-main"
                        >
                            {placeholder}
                        </TextGenericWithSkin>
                    ) : (
                        <div
                            data-testid={`${dataTestId}.tags-wrapper`}
                            className={cls(
                                'tags-wrapper',
                                {
                                    scrollable: Boolean(maxInputHeight),
                                },
                                [scrollClass],
                            )}
                            ref={tagsWrapperRef}
                        >
                            {tags.map((tagProps, index) => {
                                const defaultDataTestid =
                                    tagProps.title ?? (typeof tagProps.tag === 'string' ? tagProps.tag : index);

                                if (
                                    index === selectedTagIndex &&
                                    (isDesktop() || tagEditor?.type === TagTextInputEditor)
                                ) {
                                    return <React.Fragment key={index}>{tagEditor}</React.Fragment>;
                                }

                                const { key, ...restTagProps } = tagProps;

                                return (
                                    <TagElement
                                        key={key ?? index.toString()}
                                        {...restTagProps}
                                        tagIndex={index}
                                        onTagClick={onTagClick}
                                        data-testid={
                                            tagProps['data-testid'] ?? `${dataTestId}.tag.${defaultDataTestid}`
                                        }
                                        onTagClose={onTagClose}
                                        isEditable={isEditable}
                                        isDisabled={isDisabled || tagProps.isDisabled}
                                        size={size}
                                        className={cls('tag')}
                                    />
                                );
                            })}
                            {isTagEditorVisible &&
                                !isDisabled &&
                                !isAddDisabled &&
                                (selectedTagIndex === -1 || (isMobile() && tagEditor?.type !== TagTextInputEditor)) &&
                                tagEditor}
                            {!isTagEditorVisible && (
                                <div data-testid={`${dataTestId}.Expander`} className={cls('expander')} />
                            )}
                        </div>
                    )}
                    {addonRight ? (
                        <div className={cls('addons', [addonsStackClasses])}>
                            {counter}
                            <div
                                className={cls('addon', [addonStackClasses])}
                                data-testid={`${dataTestId}.addon`}
                                onClick={onAddonClick}
                            >
                                {addonRight}
                            </div>
                        </div>
                    ) : (
                        counter
                    )}
                    {!isTagEditorVisible && tagEditorIsExists && !isAddDisabled ? (
                        <button
                            {...focusProps}
                            aria-hidden
                            disabled={isDisabled}
                            className={cls('tag-editor-launcher')}
                            data-testid={`${dataTestId}.launcher`}
                            id={tagLauncherId}
                        />
                    ) : null}
                </div>
            </TagGroupContext.Provider>
        </LogProvider>
    );
});
